import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Globe, BookOpen, Loader2, ArrowLeft, Headphones,
  CheckCircle, Filter, ChevronRight, Trophy, Target, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const ESOLVideoLessons = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user } = useAuth();
  const isAdminPreview = location.pathname.includes('/admin/preview');
  
  const [topics, setTopics] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [progress, setProgress] = useState(null);

  const getBasePath = () => isAdminPreview ? '/admin/preview/esol' : '/student/esol';
  const getDashboardPath = () => isAdminPreview ? '/admin' : '/student';

  useEffect(() => {
    fetchData();
  }, [selectedLevel]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const requests = [
        api.get(`/v2/esol/topics${selectedLevel ? `?level=${selectedLevel}` : ''}`),
        api.get('/v2/esol/levels')
      ];
      
      // Fetch progress for logged-in students
      if (user?.id && !isAdminPreview) {
        requests.push(api.get(`/v2/esol/progress/${user.id}`));
      }
      
      const responses = await Promise.all(requests);
      setTopics(responses[0].data.topics);
      setLevels(responses[1].data.levels);
      
      if (responses[2]) {
        setProgress(responses[2].data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load ESOL topics');
    } finally {
      setLoading(false);
    }
  };

  const getTopicProgress = (topicId) => {
    if (!progress?.topics) return null;
    return progress.topics.find(t => t.topic_id === topicId);
  };

  const topicIcons = {
    'greetings': '👋', 'numbers': '🔢', 'colors': '🎨', 'family': '👨‍👩‍👧‍👦',
    'food': '🍎', 'body': '🙋', 'classroom': '📚', 'weather': '☀️',
    'directions': '🧭', 'time': '⏰', 'shopping': '🛒', 'transport': '🚌',
    'home': '🏠', 'jobs': '👷', 'emotions': '😊', 'daily': '📅',
    'health': '🏥', 'environment': '🌍', 'technology': '💻', 'sports': '⚽',
    'restaurant': '🍽️', 'holidays': '🎉', 'work': '💼', 'news': '📰',
    'education': '🎓', 'housing': '🏢', 'clothes': '👔', 'travel': '✈️',
    'business': '💼', 'media': '📺', 'culture': '🌍', 'politics': '🏛️',
    'science': '🔬', 'arts': '🎨'
  };

  const getIconForTopic = (topicId) => {
    for (const [key, icon] of Object.entries(topicIcons)) {
      if (topicId.includes(key)) return icon;
    }
    return '📖';
  };

  const levelColors = {
    'A1': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', gradient: 'from-green-400 to-emerald-500' },
    'A2': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', gradient: 'from-blue-400 to-cyan-500' },
    'B1': { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', gradient: 'from-purple-400 to-violet-500' },
    'B2': { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', gradient: 'from-orange-400 to-amber-500' }
  };

  return (
    <StudentLayout>
      <div data-testid="esol-lessons" className="max-w-5xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => navigate(getDashboardPath())}
          className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="student-card bg-gradient-to-r from-blue-500 to-cyan-500 text-white mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Globe className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="font-heading text-3xl font-bold">ESOL Lessons</h1>
              <p className="text-blue-100">Learn English with interactive lessons and audio!</p>
            </div>
          </div>
        </div>

        {/* Progress Summary Card */}
        {progress && (
          <div className="student-card mb-6" data-testid="progress-summary">
            <div className="flex items-center gap-3 mb-4">
              <Trophy className="w-6 h-6 text-amber-500" />
              <h2 className="font-bold text-slate-800">Your ESOL Progress</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-emerald-600">{progress.overall.completed_topics}/{progress.overall.total_topics}</div>
                <div className="text-sm text-slate-600">Lessons Complete</div>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{progress.overall.overall_mastery}%</div>
                <div className="text-sm text-slate-600">Overall Mastery</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{progress.overall.total_vocabulary_learned}</div>
                <div className="text-sm text-slate-600">Words Learned</div>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-amber-600">{progress.overall.accuracy}%</div>
                <div className="text-sm text-slate-600">Exercise Accuracy</div>
              </div>
            </div>
            
            {/* Level Progress Bars */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {['A1', 'A2', 'B1', 'B2'].map(level => {
                const levelData = progress.by_level[level];
                const percentage = levelData.total > 0 ? (levelData.completed / levelData.total) * 100 : 0;
                const colors = levelColors[level];
                return (
                  <div key={level} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-bold ${colors?.text}`}>{level}</span>
                      <span className="text-xs text-slate-500">{levelData.completed}/{levelData.total}</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${colors?.gradient} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Level Filter */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-5 h-5 text-slate-500" />
            <span className="font-semibold text-slate-700">Filter by Level:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedLevel(null)}
              className={`px-4 py-2 rounded-full font-semibold transition-all ${
                !selectedLevel 
                  ? 'bg-slate-800 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="filter-all"
            >
              All Levels ({topics.length})
            </button>
            {levels.map(level => (
              <button
                key={level.id}
                onClick={() => setSelectedLevel(level.id)}
                className={`px-4 py-2 rounded-full font-semibold transition-all border-2 ${
                  selectedLevel === level.id 
                    ? `${levelColors[level.id]?.bg} ${levelColors[level.id]?.text} ${levelColors[level.id]?.border}` 
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`filter-${level.id}`}
              >
                {level.id} - {level.name}
              </button>
            ))}
          </div>
        </div>

        {/* Topics Grid by Level */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="space-y-8">
            {['A1', 'A2', 'B1', 'B2'].map(level => {
              const levelTopics = topics.filter(t => t.level === level);
              if (levelTopics.length === 0) return null;
              
              const levelInfo = levels.find(l => l.id === level);
              const colors = levelColors[level];
              
              return (
                <div key={level} data-testid={`level-${level}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`px-3 py-1 rounded-lg font-bold ${colors?.bg} ${colors?.text}`}>
                      {level}
                    </span>
                    <span className="font-semibold text-slate-700">{levelInfo?.name}</span>
                    <span className="text-slate-400 text-sm">({levelTopics.length} topics)</span>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {levelTopics.map((topic) => {
                      const topicProgress = getTopicProgress(topic.id);
                      const isCompleted = topicProgress?.completed;
                      const mastery = topicProgress?.mastery || 0;
                      
                      return (
                        <div
                          key={topic.id}
                          onClick={() => navigate(`${getBasePath()}/${topic.id}`)}
                          className={`student-card cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 group ${isCompleted ? 'ring-2 ring-emerald-300' : ''}`}
                          data-testid={`topic-${topic.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${colors?.bg} relative`}>
                              {getIconForTopic(topic.id)}
                              {isCompleted && (
                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                                  <CheckCircle className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-slate-800 truncate group-hover:text-purple-600 transition-colors">
                                  {topic.topic}
                                </h3>
                              </div>
                              <p className="text-sm text-slate-500 line-clamp-2">{topic.description}</p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                                <span className="flex items-center gap-1">
                                  <BookOpen className="w-3 h-3" />
                                  {topic.vocabulary_count} words
                                </span>
                                {topic.has_audio && (
                                  <span className="flex items-center gap-1 text-cyan-500">
                                    <Headphones className="w-3 h-3" />
                                    Audio
                                  </span>
                                )}
                              </div>
                              {/* Mastery Progress Bar */}
                              {mastery > 0 && (
                                <div className="mt-2">
                                  <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-slate-500">Mastery</span>
                                    <span className={`font-medium ${mastery >= 70 ? 'text-emerald-600' : 'text-slate-600'}`}>{mastery}%</span>
                                  </div>
                                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full transition-all ${mastery >= 70 ? 'bg-emerald-500' : mastery >= 40 ? 'bg-amber-500' : 'bg-slate-400'}`}
                                      style={{ width: `${mastery}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-purple-500 transition-colors flex-shrink-0" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info Card */}
        <div className="mt-8 student-card bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-slate-800 mb-1">CEFR Levels Explained</h3>
              <div className="text-sm text-slate-600 space-y-1">
                <p><strong className="text-green-700">A1 (Beginner):</strong> Basic words and phrases for everyday situations</p>
                <p><strong className="text-blue-700">A2 (Elementary):</strong> Simple sentences about familiar topics</p>
                <p><strong className="text-purple-700">B1 (Intermediate):</strong> Main points on familiar matters like travel and work</p>
                <p><strong className="text-orange-700">B2 (Upper Intermediate):</strong> Complex texts and abstract topics</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StudentLayout>
  );
};

export default ESOLVideoLessons;
